import { ApiError } from './ApiError';
import { getAccessToken, redirectToLogin, refreshAccessToken } from './authToken';

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  signal?: AbortSignal;
  skipAuth?: boolean;
}

// JurorQuestionnaire.Api serves every route under this prefix (see
// QuestionnairesController/JurorSessionController's [RoutePrefix]). Appended
// here rather than requiring it in VITE_API_BASE_URL so a bare host (e.g.
// https://localhost:44382/) still hits the real routes instead of 404ing.
const API_ROUTE_PREFIX = '/api/v1/juror-questionnaire';

const rawBaseUrl = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');
const baseUrl =
  rawBaseUrl && !rawBaseUrl.endsWith(API_ROUTE_PREFIX)
    ? `${rawBaseUrl}${API_ROUTE_PREFIX}`
    : rawBaseUrl;

const buildUrl = (path: string): string => {
  if (path.startsWith('http')) return path;
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${normalized}`;
};

const isPassthroughBody = (body: unknown): body is BodyInit =>
  typeof FormData !== 'undefined' && body instanceof FormData ||
  typeof Blob !== 'undefined' && body instanceof Blob ||
  typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams ||
  typeof ArrayBuffer !== 'undefined' &&
    (body instanceof ArrayBuffer ||
      ArrayBuffer.isView(body as ArrayBufferView));

const serializeBody = (body: unknown): BodyInit | undefined => {
  if (body === undefined) return undefined;
  if (isPassthroughBody(body)) return body;
  return JSON.stringify(body);
};

// Tenant id is stored in localStorage so it survives reloads. The
// AppHeader picker writes it; every API call reads it from here so we
// never have to thread it through call sites.
const TENANT_STORAGE_KEY = 'jp-tenant-id';

export const getTenantId = (): string | null => {
  try {
    return localStorage.getItem(TENANT_STORAGE_KEY);
  } catch {
    return null;
  }
};

export const setTenantId = (id: string | null): void => {
  try {
    if (id) localStorage.setItem(TENANT_STORAGE_KEY, id);
    else localStorage.removeItem(TENANT_STORAGE_KEY);
  } catch {
    /* no-op */
  }
};

const buildHeaders = (
  init: RequestOptions,
  accessToken: string | null,
): Headers => {
  const headers = new Headers(init.headers);
  // Only set application/json when the caller hasn't already set the header
  // and the body is JSON-serializable. FormData/Blob carry their own boundary
  // or content-type that fetch will set automatically.
  if (
    init.body !== undefined &&
    !isPassthroughBody(init.body) &&
    !headers.has('Content-Type')
  ) {
    headers.set('Content-Type', 'application/json');
  }
  if (accessToken && !init.skipAuth) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }
  // Send the tenant id on every request unless the caller already set
  // one (lets the New Questionnaire flow override during create).
  const tenantId = getTenantId();
  if (tenantId && !headers.has('X-Tenant-Id')) {
    headers.set('X-Tenant-Id', tenantId);
  }
  return headers;
};

const parseError = async (response: Response): Promise<ApiError> => {
  const requestId = response.headers.get('x-request-id') ?? undefined;
  let body: { code?: string; message?: string } = {};
  try {
    body = (await response.json()) as typeof body;
  } catch {
    // body wasn't JSON — leave body empty
  }
  return new ApiError({
    status: response.status,
    code: body.code ?? `HTTP_${response.status}`,
    message: body.message ?? response.statusText ?? 'Request failed',
    requestId,
  });
};

const performFetch = async <T>(
  path: string,
  method: string,
  init: RequestOptions,
  retry = true,
): Promise<T> => {
  const accessToken = await getAccessToken();
  const headers = buildHeaders(init, accessToken);
  const response = await fetch(buildUrl(path), {
    ...init,
    method,
    headers,
    body: serializeBody(init.body),
  });

  if (response.status === 401 && retry && !init.skipAuth) {
    const refreshed = await refreshAccessToken();
    if (refreshed) return performFetch<T>(path, method, init, false);
    redirectToLogin();
    throw new ApiError({
      status: 401,
      code: 'UNAUTHENTICATED',
      message: 'Session expired',
    });
  }

  if (!response.ok) {
    throw await parseError(response);
  }

  if (response.status === 204) return undefined as T;
  const text = await response.text();
  return text ? (JSON.parse(text) as T) : (undefined as T);
};

export const apiClient = {
  get: <T>(path: string, init: RequestOptions = {}) =>
    performFetch<T>(path, 'GET', init),
  post: <T>(path: string, body?: unknown, init: RequestOptions = {}) =>
    performFetch<T>(path, 'POST', { ...init, body }),
  put: <T>(path: string, body?: unknown, init: RequestOptions = {}) =>
    performFetch<T>(path, 'PUT', { ...init, body }),
  patch: <T>(path: string, body?: unknown, init: RequestOptions = {}) =>
    performFetch<T>(path, 'PATCH', { ...init, body }),
  delete: <T>(path: string, init: RequestOptions = {}) =>
    performFetch<T>(path, 'DELETE', init),
};

export type ApiClient = typeof apiClient;
